/**
 * One order of the order history.
 *
 * Markup contract (page/account/inc/order_single.html.twig):
 *   <div class="account_order_item" x-data="accountOrder({ orderNumber: 12345, showDetailsText: '…', hideDetailsText: '…' })">
 *     <button @click="toggleDetails()" x-text="open ? hideDetailsText : showDetailsText">
 *     <div class="account_order_products collapsible" :class="{ 'is-open': open }">
 *     <template x-ref="swissNote" data-title="…"> … </template>
 *
 * The invoice button is hidden until the ACL API is ready (DEV-2877); the markup
 * block and this branch stay until it is switched back on.
 */
import { aclUrl, actionToken, returnPortalUrl } from './aclOrder.js';

/** The only request here that reads its answer itself: it is a pdf, not json. */
async function fetchInvoice(orderNumber) {
    const session = Alpine.store('session');
    if (!session.loaded) {
        await session.loadPromise;
    }

    const response = await fetch(aclUrl('invoiceDownload'), {
        method: 'POST',
        credentials: 'same-origin',
        // orderId is the transport name of the endpoint; its value is the order number, never the oxid
        body: new URLSearchParams({
            orderId: orderNumber,
            actionToken: await actionToken('invoice', orderNumber),
            stoken: session.stoken,
        }),
        signal: AbortSignal.timeout(30000),
    });

    if (!response.ok) {
        throw new Error('Invoice unavailable');
    }

    // the endpoint answers with the raw pdf body …
    if ((response.headers.get('Content-Type') || '').includes('application/pdf')) {
        const blob = await response.blob();
        if (!blob.size) {
            throw new Error('Invoice unavailable');
        }
        return blob;
    }

    // … or with the compatibility envelope carrying it as base64
    const result = await response.json();
    if (!result.success || !result.data) {
        throw new Error('Invoice unavailable');
    }

    const bytes = Uint8Array.from(atob(result.data), (character) => character.charCodeAt(0));

    return new Blob([bytes], { type: 'application/pdf' });
}

function saveAs(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

document.addEventListener('alpine:init', () => {
    Alpine.data('accountOrder', ({ orderNumber, showDetailsText, hideDetailsText }) => ({
        orderNumber,
        showDetailsText,
        hideDetailsText,
        open: false,
        invoiceLoading: false,
        invoiceError: false,
        returnLoading: false,
        returnError: false,

        toggleDetails() {
            this.open = !this.open;
        },

        /** Swiss orders are returned by post, the note says how. */
        showSwissNote() {
            const note = this.$refs.swissNote;

            Alpine.store('dialog').openHtml(note.innerHTML, {
                title: note.dataset.title,
                className: 'page-dialog--narrow',
            });
        },

        async downloadInvoice() {
            if (this.invoiceLoading) {
                return;
            }

            this.invoiceLoading = true;
            this.invoiceError = false;

            try {
                saveAs(await fetchInvoice(this.orderNumber), `Rechnung-${this.orderNumber}.pdf`);
            } catch (error) {
                this.invoiceError = true;
            } finally {
                this.invoiceLoading = false;
            }
        },

        async openReturnPortal() {
            if (this.returnLoading) {
                return;
            }

            this.returnLoading = true;
            this.returnError = false;

            try {
                window.open(await returnPortalUrl({ orderNumber: this.orderNumber }), '_blank');
            } catch (error) {
                this.returnError = true;
            } finally {
                this.returnLoading = false;
            }
        },
    }));
});
